import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCalendar,
  FiCheckCircle,
  FiCreditCard,
  FiDollarSign,
  FiFileText,
  FiLoader,
  FiSave,
  FiUser,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { apiRequest } from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

const paymentModes = [
  { value: "CASH", label: "Cash" },
  { value: "UPI", label: "UPI" },
  { value: "CARD", label: "Card" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
  { value: "CHEQUE", label: "Cheque" },
  { value: "OTHER", label: "Other" },
];

const today = () => new Date().toISOString().slice(0, 10);

export default function ReceivePayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken } = useAuth();

  const invoiceIdFromState = location.state?.invoiceId || "";

  const [invoiceId, setInvoiceId] = useState(invoiceIdFromState);
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [previousPayments, setPreviousPayments] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    paymentDate: today(),
    paymentMode: "UPI",
    reference: "",
    amount: "",
    notes: "",
  });

  const updateField = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const loadInvoiceOptions = useCallback(async () => {
    setIsLoadingOptions(true);

    try {
      const response = await apiRequest("/sales-invoices", {
        accessToken,
      });

      const available = (Array.isArray(response) ? response : []).filter(
        (item) => {
          const balanceAmount = Math.max(
            Number(item.totalAmount || 0) -
              Number(item.paidAmount || 0) -
              Number(item.creditedAmount || 0),
            0,
          );

          return item.status === "ISSUED" && balanceAmount > 0;
        },
      );

      setInvoiceOptions(available);
    } catch (requestError) {
      setError(requestError.message || "Could not load open invoices.");
    } finally {
      setIsLoadingOptions(false);
    }
  }, [accessToken]);

  const loadInvoice = useCallback(
    async (selectedInvoiceId) => {
      if (!selectedInvoiceId) {
        setInvoice(null);
        setPreviousPayments([]);
        return;
      }

      setIsLoadingInvoice(true);
      setError("");

      try {
        const [invoiceResponse, paymentsResponse] = await Promise.all([
          apiRequest(`/sales-invoices/${selectedInvoiceId}`, {
            accessToken,
          }),
          apiRequest("/sales-payments", {
            accessToken,
          }),
        ]);

        const totalAmount = Number(invoiceResponse.totalAmount || 0);
        const paidAmount = Number(invoiceResponse.paidAmount || 0);
        const creditedAmount = Number(invoiceResponse.creditedAmount || 0);

        const balance = Math.max(totalAmount - paidAmount - creditedAmount, 0);

        const paymentsForInvoice = (
          Array.isArray(paymentsResponse) ? paymentsResponse : []
        ).filter((payment) =>
          payment.allocations?.some(
            (allocation) => allocation.invoiceId === selectedInvoiceId,
          ),
        );

        setInvoice(invoiceResponse);
        setPreviousPayments(paymentsForInvoice);

        setForm((current) => ({
          ...current,
          amount: balance > 0 ? String(balance) : "",
        }));
      } catch (requestError) {
        setInvoice(null);
        setPreviousPayments([]);
        setError(requestError.message || "Could not load invoice details.");
      } finally {
        setIsLoadingInvoice(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    loadInvoiceOptions();
  }, [loadInvoiceOptions]);

  useEffect(() => {
    loadInvoice(invoiceId);
  }, [invoiceId, loadInvoice]);

  const totals = useMemo(() => {
    const total = Number(invoice?.totalAmount || 0);
    const paid = Number(invoice?.paidAmount || 0);
    const credited = Number(invoice?.creditedAmount || 0);

    const balance = Math.max(total - paid - credited, 0);
    const receiving = Number(form.amount || 0);
    const remaining = Math.max(balance - receiving, 0);

    return {
      subtotal: Number(invoice?.subtotalAmount || 0),
      discount: Number(invoice?.discountAmount || 0),
      tax: Number(invoice?.taxAmount || 0),
      total,
      paid,
      credited,
      balance,
      receiving,
      remaining,
    };
  }, [invoice, form.amount]);

  const handleInvoiceChange = (value) => {
    setInvoiceId(value);
  };

  const receivePayment = async () => {
    setError("");

    if (!invoice) {
      setError("Please select an issued invoice.");
      return;
    }

    if (invoice.status !== "ISSUED") {
      setError("Payments can only be received against issued invoices.");
      return;
    }

    if (!Number.isFinite(totals.receiving) || totals.receiving <= 0) {
      setError("Enter a valid payment amount.");
      return;
    }

    if (totals.receiving > totals.balance) {
      setError(
        `Payment cannot exceed the outstanding balance of ${formatMoney(totals.balance)}.`,
      );
      return;
    }

    setIsSaving(true);

    try {
      const receipt = await apiRequest("/sales-payments", {
        method: "POST",
        accessToken,
        body: {
          paymentDate: form.paymentDate,
          paymentMode: form.paymentMode,
          reference: form.reference.trim() || undefined,
          notes: form.notes.trim() || undefined,
          allocations: [
            {
              invoiceId: invoice.id,
              amount: totals.receiving,
            },
          ],
        },
      });

      navigate(`/admindashboard/sales/invoice/${invoice.id}`, {
        replace: true,
        state: {
          successMessage: `${receipt.receiptCode} was created successfully.`,
        },
      });
    } catch (requestError) {
      setError(requestError.message || "Could not receive payment.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:justify-between xl:items-start">
        <div>
          <button
            onClick={() =>
              navigate(
                location.state?.from || "/admindashboard/sales/invoice/list",
              )
            }
            className="flex items-center gap-2 text-gray-500 hover:text-primary-700 mb-4"
          >
            <FiArrowLeft />
            Back to Invoices
          </button>

          <h1 className="text-4xl font-bold text-gray-900">Receive Payment</h1>

          <p className="text-gray-500 mt-2">
            Record a customer payment against an issued invoice.
          </p>
        </div>

        <button
          onClick={receivePayment}
          disabled={isSaving || !invoice || totals.balance <= 0}
          className="h-11 px-6 rounded-xl bg-primary-700 text-white flex items-center gap-2 disabled:opacity-60"
        >
          {isSaving ? <FiLoader className="animate-spin" /> : <FiCheckCircle />}
          Receive Payment
        </button>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-red-700 flex gap-3">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="bg-white rounded-3xl border border-gray-100 p-8">
        <h2 className="text-2xl font-bold mb-8">Select Invoice</h2>

        <div className="grid lg:grid-cols-2 gap-6">
          <Select
            label="Issued Invoice *"
            value={invoiceId}
            onChange={handleInvoiceChange}
            disabled={isLoadingOptions}
            options={[
              {
                value: "",
                label: isLoadingOptions
                  ? "Loading open invoices..."
                  : "Select an issued invoice",
              },
              ...invoiceOptions.map((item) => {
                const balance = Math.max(
                  Number(item.totalAmount || 0) -
                    Number(item.paidAmount || 0) -
                    Number(item.creditedAmount || 0),
                  0,
                );

                return {
                  value: item.id,
                  label: `${item.invoiceCode} — ${
                    item.customerNameSnapshot
                  } — Balance ${formatMoney(balance)}`,
                };
              }),
            ]}
          />

          <Input
            label="Sales Order"
            value={invoice?.salesOrder?.salesOrderNumber || "Auto-filled"}
            disabled
          />
        </div>
      </div>

      <div className="grid xl:grid-cols-4 gap-5">
        <StatusCard
          title="Invoice"
          value={invoice?.invoiceCode || "Not selected"}
          icon={<FiFileText size={22} />}
          color="bg-blue-50 text-blue-700"
        />

        <StatusCard
          title="Customer"
          value={invoice?.customerNameSnapshot || "Not selected"}
          icon={<FiUser size={22} />}
          color="bg-green-50 text-green-700"
        />

        <StatusCard
          title="Outstanding"
          value={invoice ? formatMoney(totals.balance) : "—"}
          icon={<FiDollarSign size={22} />}
          color="bg-red-50 text-red-700"
        />

        <StatusCard
          title="Payment Mode"
          value={toTitle(form.paymentMode)}
          icon={<FiCreditCard size={22} />}
          color="bg-purple-50 text-purple-700"
        />
      </div>

      {isLoadingInvoice ? (
        <div className="bg-white rounded-3xl border border-gray-100 p-16 text-center text-primary-700">
          <FiLoader className="inline animate-spin mr-2" />
          Loading invoice information...
        </div>
      ) : (
        <>
          <div className="bg-white rounded-3xl border border-gray-100 p-8">
            <h2 className="text-2xl font-bold mb-8">Invoice Information</h2>

            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <Info label="Invoice Number" value={invoice?.invoiceCode} />
                <Info label="Customer" value={invoice?.customerNameSnapshot} />
                <Info label="Phone" value={invoice?.customerPhoneSnapshot} />
                <Info label="Email" value={invoice?.customerEmailSnapshot} />
              </div>

              <div>
                <Info
                  label="Invoice Date"
                  value={invoice ? formatDate(invoice.invoiceDate) : "—"}
                />
                <Info
                  label="Due Date"
                  value={invoice?.dueDate ? formatDate(invoice.dueDate) : "—"}
                />
                <Info label="Store" value={invoice?.store?.name} />
                <Info
                  label="Outstanding"
                  value={invoice ? formatMoney(totals.balance) : "—"}
                  valueClassName="text-red-600"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-gray-100 p-8">
            <h2 className="text-2xl font-bold mb-8">Payment Information</h2>

            <div className="grid lg:grid-cols-3 gap-6">
              <Input
                type="date"
                label="Receipt Date"
                value={form.paymentDate}
                onChange={(value) => updateField("paymentDate", value)}
              />

              <Select
                label="Payment Mode"
                value={form.paymentMode}
                onChange={(value) => updateField("paymentMode", value)}
                options={paymentModes}
              />

              <Input
                label="Reference Number"
                value={form.reference}
                placeholder="UPI / cheque / bank reference"
                onChange={(value) => updateField("reference", value)}
              />

              <Input
                type="number"
                min="0.01"
                max={totals.balance || undefined}
                step="0.01"
                label="Amount Received *"
                value={form.amount}
                onChange={(value) => updateField("amount", value)}
              />

              <div className="lg:col-span-2">
                <p className="text-sm text-gray-500 mt-8">
                  A receipt code is generated automatically after payment is
                  recorded.
                </p>
              </div>
            </div>
          </div>

          <div className="grid xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 space-y-6">
              <div className="bg-white rounded-3xl border border-gray-100 p-8">
                <h2 className="text-2xl font-bold mb-8">Invoice Summary</h2>

                <div className="grid md:grid-cols-2 gap-6">
                  <Summary
                    label="Subtotal"
                    value={formatMoney(totals.subtotal)}
                  />
                  <Summary
                    label="Discount"
                    value={formatMoney(totals.discount)}
                  />
                  <Summary label="GST" value={formatMoney(totals.tax)} />
                  <Summary
                    label="Grand Total"
                    value={formatMoney(totals.total)}
                  />
                  <Summary
                    label="Already Paid"
                    value={formatMoney(totals.paid)}
                  />
                  <Summary
                    label="Credit Notes"
                    value={formatMoney(totals.credited)}
                  />
                  <Summary
                    label="Outstanding"
                    value={formatMoney(totals.balance)}
                  />
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
                <div className="px-8 py-6 border-b">
                  <h2 className="text-2xl font-bold">Payment Allocation</h2>
                  <p className="text-gray-500 mt-2">
                    This receipt will be allocated to the selected invoice.
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <TableHeader>Invoice</TableHeader>
                        <TableHeader>Date</TableHeader>
                        <TableHeader right>Total</TableHeader>
                        <TableHeader right>Paid</TableHeader>
                        <TableHeader right>Balance</TableHeader>
                        <TableHeader center>Allocate</TableHeader>
                      </tr>
                    </thead>

                    <tbody>
                      {invoice ? (
                        <tr className="border-b">
                          <td className="px-5 py-5 font-semibold">
                            {invoice.invoiceCode}
                          </td>
                          <td className="px-5 py-5">
                            {formatDate(invoice.invoiceDate)}
                          </td>
                          <td className="px-5 py-5 text-right">
                            {formatMoney(totals.total)}
                          </td>
                          <td className="px-5 py-5 text-right text-green-600">
                            {formatMoney(totals.paid)}
                          </td>
                          <td className="px-5 py-5 text-right font-semibold text-red-600">
                            {formatMoney(totals.balance)}
                          </td>
                          <td className="px-5 py-5 text-center">
                            <input
                              type="number"
                              min="0.01"
                              max={totals.balance}
                              step="0.01"
                              value={form.amount}
                              onChange={(event) =>
                                updateField("amount", event.target.value)
                              }
                              className="w-36 h-11 rounded-xl border border-gray-200 px-4 text-center focus:outline-none focus:border-primary-700"
                            />
                          </td>
                        </tr>
                      ) : (
                        <tr>
                          <td
                            colSpan="6"
                            className="px-5 py-14 text-center text-gray-500"
                          >
                            Select an invoice to allocate payment.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-gray-100 p-8">
                <h2 className="text-2xl font-bold mb-6">Payment Notes</h2>

                <textarea
                  rows={6}
                  value={form.notes}
                  onChange={(event) => updateField("notes", event.target.value)}
                  placeholder="Enter notes about this payment..."
                  className="w-full rounded-2xl border border-gray-200 p-4 resize-none focus:outline-none focus:border-primary-700"
                />
              </div>
            </div>

            <div>
              <div className="sticky top-6 bg-white rounded-3xl border border-gray-100 p-6">
                <h2 className="text-2xl font-bold">Payment Summary</h2>

                <div className="space-y-5 mt-8">
                  <Summary
                    label="Invoice Total"
                    value={formatMoney(totals.total)}
                  />
                  <Summary
                    label="Already Paid"
                    value={formatMoney(totals.paid)}
                  />
                  <Summary
                    label="Outstanding"
                    value={formatMoney(totals.balance)}
                  />

                  <div className="border-t pt-5">
                    <Summary
                      label="Receiving Today"
                      value={formatMoney(totals.receiving)}
                    />
                  </div>

                  <Summary
                    label="Remaining Balance"
                    value={formatMoney(totals.remaining)}
                  />
                </div>

                <div className="border-t mt-8 pt-6">
                  <button
                    onClick={receivePayment}
                    disabled={
                      isSaving ||
                      !invoice ||
                      totals.receiving <= 0 ||
                      totals.receiving > totals.balance
                    }
                    className="w-full h-12 rounded-xl bg-primary-700 text-white font-medium flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {isSaving ? (
                      <FiLoader className="animate-spin" />
                    ) : (
                      <FiSave />
                    )}
                    Receive Payment
                  </button>
                </div>
              </div>
            </div>
          </div>

          <PaymentHistory payments={previousPayments} invoiceId={invoice?.id} />
        </>
      )}
    </div>
  );
}

function PaymentHistory({ payments, invoiceId }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
      <div className="px-8 py-6 border-b">
        <h2 className="text-2xl font-bold">Previous Payments</h2>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[750px]">
          <thead className="bg-gray-50">
            <tr>
              <TableHeader>Receipt No.</TableHeader>
              <TableHeader>Date</TableHeader>
              <TableHeader>Payment Mode</TableHeader>
              <TableHeader>Reference</TableHeader>
              <TableHeader right>Allocated Amount</TableHeader>
              <TableHeader center>Status</TableHeader>
            </tr>
          </thead>

          <tbody>
            {payments.length ? (
              payments.map((payment) => {
                const allocation = payment.allocations?.find(
                  (item) => item.invoiceId === invoiceId,
                );

                return (
                  <tr key={payment.id} className="border-b last:border-b-0">
                    <td className="px-5 py-5 font-semibold">
                      {payment.receiptCode}
                    </td>
                    <td className="px-5 py-5">
                      {formatDate(payment.paymentDate)}
                    </td>
                    <td className="px-5 py-5">
                      {toTitle(payment.paymentMode)}
                    </td>
                    <td className="px-5 py-5">{payment.reference || "—"}</td>
                    <td className="px-5 py-5 text-right font-semibold text-green-600">
                      {formatMoney(allocation?.amount)}
                    </td>
                    <td className="px-5 py-5 text-center">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          payment.status === "COMPLETED"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {toTitle(payment.status)}
                      </span>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan="6"
                  className="px-5 py-14 text-center text-gray-500"
                >
                  No payments have been recorded for this invoice.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusCard({ title, value, icon, color }) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6">
      <div className="flex justify-between items-center gap-3">
        <div className="min-w-0">
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="text-xl font-bold mt-2 truncate">{value}</h3>
        </div>

        <div
          className={`h-14 w-14 shrink-0 rounded-2xl flex items-center justify-center ${color}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  disabled = false,
  min,
  max,
  step,
}) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>

      <input
        type={type}
        value={value || ""}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        onChange={(event) => onChange?.(event.target.value)}
        className="w-full h-12 border border-gray-200 rounded-xl px-4 focus:outline-none focus:border-primary-700 disabled:bg-gray-50 disabled:text-gray-500"
      />
    </div>
  );
}

function Select({ label, value, onChange, options, disabled = false }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>

      <select
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="w-full h-12 border border-gray-200 rounded-xl px-4 bg-white focus:outline-none focus:border-primary-700 disabled:bg-gray-50"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function Info({ label, value, valueClassName = "" }) {
  return (
    <div className="flex justify-between gap-5 py-4 border-b border-gray-100 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className={`font-semibold text-right ${valueClassName}`}>
        {value || "—"}
      </span>
    </div>
  );
}

function Summary({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-right">{value}</span>
    </div>
  );
}

function TableHeader({ children, right = false, center = false }) {
  return (
    <th
      className={`px-5 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500 ${
        right ? "text-right" : center ? "text-center" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function formatMoney(value) {
  return `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return "—";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function toTitle(value = "") {
  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
